import React from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Chip,
  Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Close, Restaurant, Warning } from '@mui/icons-material';
import MenuAnalysisResults from './MenuAnalysisResults';
import HealthContextSection from './HealthContextSection';

const MenuAnalysisModal = ({ open, onClose, analysisData, isLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation();
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#4CAF50';
      case 'failed':
        return '#f44336';
      default:
        return '#FF9800';
    }
  };

  if (!analysisData) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="menu-analysis-modal"
      aria-describedby="menu-analysis-details"
      BackdropProps={{ onClick: () => {} }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: isMobile ? '95%' : '800px',
          maxHeight: '90vh',
          p: 4,
          bgcolor: 'background.paper',
          borderRadius: 2,
          position: 'relative',
          // top: '50%',
          // left: '50%',
          // transform: 'translate(-50%, -50%)',
          overflow: 'auto',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            pb: 2,
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
            <Restaurant sx={{ color: '#26786A', fontSize: isMobile ? 24 : 28 }} />
            <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 600, color: '#333' }}>
              {t('menuAnalysisDetails')}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#666' }}>
            <Close />
          </IconButton>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress style={{ color: '#26786a' }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Basic Information */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
                {t('analysisInformation')}
              </Typography>
              <Grid container spacing={isMobile ? 1 : 2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                    {t('fileName')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#333', wordBreak: 'break-word' }}>
                    {analysisData.fileName || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                    {t('uploadDate')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#333' }}>
                    {formatDate(analysisData.uploadDate)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                    {t('foodAnalysisObj.status')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: getStatusColor(analysisData.status),
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: getStatusColor(analysisData.status),
                        textTransform: 'capitalize',
                      }}
                    >
                      {t(`foodAnalysisObj.${analysisData.status}`)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                    {t('provider')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#333' }}>
                    {analysisData.analysisProvider || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Menu Analysis Results */}
            {analysisData.status === 'completed' && (
              <>
                <Divider />
                <MenuAnalysisResults analysis={analysisData} compact={false} />
              </>
            )}

            {/* Error Message */}
            {analysisData.status === 'failed' && analysisData.errorMessage && (
              <>
                <Divider />
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Warning sx={{ color: '#f44336', mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#f44336' }}>
                      Analysis Error
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                      {analysisData.errorMessage}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
            {analysisData.healthContextSnapshot && (
              <>
                <Divider />
                <HealthContextSection healthContextSnapshot={analysisData.healthContextSnapshot} />
              </>
            )}
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default MenuAnalysisModal;
